import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/components/feature/AdminAuthContext';
import { getSupabase } from '@/lib/supabase';
import Logo from '@/components/feature/Logo';

interface AppRow {
  id: string;
  reference_number: string;
  client_full_name: string;
  country_of_travel: string;
  visa_type: string;
  final_decision: string;
  current_stage: number;
  submitted_at: string;
}

type ReportTab = 'overview' | 'by-country' | 'by-visa-type' | 'approved' | 'declined' | 'pending';

export default function Reports() {
  const { staffName, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [apps, setApps] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<ReportTab>('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const supabase = getSupabase();
      const { data, error: fetchErr } = await supabase
        .from('applications')
        .select('id, reference_number, client_full_name, country_of_travel, visa_type, final_decision, current_stage, submitted_at')
        .order('submitted_at', { ascending: false });
      if (fetchErr) throw fetchErr;
      setApps(data || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const total = apps.length;
  const approved = apps.filter((a) => a.final_decision === 'approved').length;
  const declined = apps.filter((a) => a.final_decision === 'declined').length;
  const pending = apps.filter((a) => a.final_decision === 'pending').length;

  const byCountry: Record<string, { total: number; approved: number; declined: number; pending: number }> = {};
  const byVisaType: Record<string, { total: number; approved: number; declined: number; pending: number }> = {};
  apps.forEach((a) => {
    if (!byCountry[a.country_of_travel]) byCountry[a.country_of_travel] = { total: 0, approved: 0, declined: 0, pending: 0 };
    byCountry[a.country_of_travel].total++;
    if (a.final_decision === 'approved') byCountry[a.country_of_travel].approved++;
    else if (a.final_decision === 'declined') byCountry[a.country_of_travel].declined++;
    else byCountry[a.country_of_travel].pending++;

    if (!byVisaType[a.visa_type]) byVisaType[a.visa_type] = { total: 0, approved: 0, declined: 0, pending: 0 };
    byVisaType[a.visa_type].total++;
    if (a.final_decision === 'approved') byVisaType[a.visa_type].approved++;
    else if (a.final_decision === 'declined') byVisaType[a.visa_type].declined++;
    else byVisaType[a.visa_type].pending++;
  });

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const tabs: { key: ReportTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'ri-pie-chart-line' },
    { key: 'by-country', label: 'By Country', icon: 'ri-global-line' },
    { key: 'by-visa-type', label: 'By Visa Type', icon: 'ri-file-text-line' },
    { key: 'approved', label: 'Approved', icon: 'ri-check-double-line' },
    { key: 'declined', label: 'Declined', icon: 'ri-close-circle-line' },
    { key: 'pending', label: 'Pending', icon: 'ri-hourglass-line' },
  ];

  const renderTable = (headers: string[], rows: { data: (string | number)[] }[]) => (
    <div className="bg-background-50 border border-secondary-200 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-secondary-200 bg-secondary-50">
              {headers.map((h) => (<th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-foreground-600 whitespace-nowrap">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="border-b border-secondary-100 hover:bg-secondary-50 transition-colors">
                {row.data.map((cell, j) => (<td key={j} className={`px-4 py-2.5 text-sm whitespace-nowrap ${j === 0 ? 'font-medium text-foreground-900' : 'text-foreground-600'}`}>{cell}</td>))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-primary-900 sticky top-0 z-40">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-14 md:h-16">
            <div className="flex items-center gap-3">
              <Logo variant="light" className="w-[130px] md:w-[170px]" />
              <span className="hidden sm:block font-heading font-bold text-sm md:text-base text-background-50 whitespace-nowrap">PVS Admin</span>
            </div>
            <div className="hidden md:flex items-center gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 hover:text-background-50 hover:bg-background-50/10 rounded-md transition-colors whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:block text-background-50/80 text-sm">{staffName}</span>
              <button onClick={handleLogout} className="px-3 py-1.5 bg-background-50/10 text-background-50 text-xs font-medium rounded-md border border-background-50/20 hover:bg-background-50/20 transition-colors whitespace-nowrap cursor-pointer">Logout</button>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-8 h-8 flex items-center justify-center text-background-50 cursor-pointer"><i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i></button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-3 flex flex-col gap-1">
              <Link to="/admin" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Dashboard</Link>
              <Link to="/admin/appointments" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Appointments</Link>
              <Link to="/admin/applications" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">All Applications</Link>
              <Link to="/admin/reports" className="px-3 py-2 text-sm font-medium text-background-50 bg-background-50/10 rounded-md whitespace-nowrap cursor-pointer">Reports</Link>
              <Link to="/admin/settings" className="px-3 py-2 text-sm font-medium text-background-50/70 rounded-md whitespace-nowrap cursor-pointer">Settings</Link>
            </div>
          )}
        </div>
      </header>

      <main className="py-8 md:py-10">
        <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">Reports</h2>
            <p className="text-sm text-foreground-600 mt-1">Application analytics and statistics</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-6 flex items-center gap-2"><i className="ri-error-warning-line"></i> {error}<button onClick={fetchApps} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">Retry</button></div>
          )}

          {loading ? (
            <div className="text-center py-20"><i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i><p className="text-sm text-foreground-500 mt-3">Loading reports...</p></div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mb-8">
                {[
                  { label: 'Total', value: total, border: 'border-secondary-200', color: 'text-foreground-900' },
                  { label: 'Approved', value: approved, border: 'border-green-200', color: 'text-green-700' },
                  { label: 'Declined', value: declined, border: 'border-red-200', color: 'text-red-700' },
                  { label: 'Pending', value: pending, border: 'border-amber-200', color: 'text-amber-700' },
                ].map((s) => (
                  <div key={s.label} className={`bg-background-50 border ${s.border} rounded-lg p-4`}>
                    <p className="text-xs text-foreground-500">{s.label}</p>
                    <p className={`text-2xl font-heading font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                {tabs.map((tab) => (
                  <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${activeTab === tab.key ? 'bg-primary-500 text-background-50' : 'bg-secondary-100 text-foreground-600 hover:bg-secondary-200'}`}>
                    <i className={tab.icon}></i>{tab.label}
                  </button>
                ))}
              </div>

              {activeTab === 'overview' && (
                <div className="bg-background-50 border border-secondary-200 rounded-lg p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-base text-foreground-900 mb-4">Monthly Overview - {monthNames[new Date().getMonth()]} 2026</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Total Applications', value: total, bg: 'bg-secondary-50', color: 'text-foreground-900' },
                      { label: 'Approved', value: approved, bg: 'bg-green-50', color: 'text-green-700' },
                      { label: 'Declined', value: declined, bg: 'bg-red-50', color: 'text-red-700' },
                      { label: 'Pending', value: pending, bg: 'bg-amber-50', color: 'text-amber-700' },
                    ].map((s) => (
                      <div key={s.label} className={`text-center p-4 ${s.bg} rounded-lg`}>
                        <p className="text-xs text-foreground-500">{s.label}</p>
                        <p className={`text-xl font-heading font-bold ${s.color}`}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'by-country' && renderTable(
                ['Country', 'Total', 'Approved', 'Declined', 'Pending', 'Approval Rate'],
                Object.entries(byCountry).sort(([, a], [, b]) => b.total - a.total).map(([c, d]) => ({ data: [c, d.total, d.approved, d.declined, d.pending, `${d.total > 0 ? Math.round((d.approved / d.total) * 100) : 0}%`] }))
              )}

              {activeTab === 'by-visa-type' && renderTable(
                ['Visa Type', 'Total', 'Approved', 'Declined', 'Pending', 'Approval Rate'],
                Object.entries(byVisaType).sort(([, a], [, b]) => b.total - a.total).map(([t, d]) => ({ data: [t, d.total, d.approved, d.declined, d.pending, `${d.total > 0 ? Math.round((d.approved / d.total) * 100) : 0}%`] }))
              )}

              {activeTab === 'approved' && renderTable(
                ['Reference', 'Client', 'Country', 'Visa Type', 'Date'],
                apps.filter((a) => a.final_decision === 'approved').map((a) => ({ data: [a.reference_number, a.client_full_name, a.country_of_travel, a.visa_type, a.submitted_at?.split('T')[0]] }))
              )}

              {activeTab === 'declined' && renderTable(
                ['Reference', 'Client', 'Country', 'Visa Type', 'Date'],
                apps.filter((a) => a.final_decision === 'declined').map((a) => ({ data: [a.reference_number, a.client_full_name, a.country_of_travel, a.visa_type, a.submitted_at?.split('T')[0]] }))
              )}

              {activeTab === 'pending' && renderTable(
                ['Reference', 'Client', 'Country', 'Visa Type', 'Date'],
                apps.filter((a) => a.final_decision === 'pending').map((a) => ({ data: [a.reference_number, a.client_full_name, a.country_of_travel, a.visa_type, a.submitted_at?.split('T')[0]] }))
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}